import {getLocalizationBasedOnLang} from '../../data/models/User/UserInfo';

/*
 * The words of Studio's create page: its form, the voice library, a source video's preview and
 * the upload of a source video. Choices the Studio server sends (lengths, endings, styles,
 * deliveries) arrive in its own words and are shown as they are; the fallback choices below
 * stand in for them when the server's list can't be read.
 */

const english = {
  // Page
  pageTitle: 'Create a microreel',
  lead: 'Review and edit your storyboard before generating your microreel. AI credits are used when creating or regenerating content.',
  project: (name: string) => `Project: ${name}`,
  cancel: 'Cancel',
  createStoryboard: 'Create storyboard',
  findTopics: 'Find topics',
  starting: 'Starting…',
  uploadingVideo: 'Uploading video…',

  // Source card
  source: 'Source',
  sourcePrompt: 'A topic or prompt',
  sourcePromptHint: 'Describe a topic in a few lines',
  sourceFile: 'Upload a file',
  sourceFileHint: 'PDF, Word or PowerPoint',
  sourceFileVideoHint: 'PDF, Word, PowerPoint or MP4 video',
  hintPrompt: 'We’ll write the microreel from your prompt.',
  hintClipping: 'Amount of data + length = amount of reels',
  hintVideo: 'We’ll write the microreel from what’s said in your video.',
  hintDocumentOrVideo: 'We’ll write the microreel from your document or video.',
  hintDocument:
    'Your workspace colors and logo (Settings) and your document’s style guide the branding.',
  promptLabel: 'What should this microreel cover?',
  promptPlaceholder:
    'e.g. Explain how to respond to a fire alarm, including evacuation routes, assembly points, and who to contact.',
  emptyTitle: 'Have content to build from?',
  dropDocument: 'Drop a document here or browse',
  dropDocumentOrVideo: 'Drop a document or video here, or browse',
  uploadFile: 'Upload file',
  formatsDocumentsAndVideos: 'PDF, DOCX, PPTX or MP4',
  limitsDocumentsAndVideos: (documentMb: number, videoMb: number) =>
    `Documents up to ${documentMb} MB · videos up to ${videoMb} MB`,
  formatsDocuments: (documentMb: number) =>
    `PDF, DOCX or PPTX · up to ${documentMb} MB`,
  kilobytes: (count: number) => `${count} KB`,
  megabytes: (count: string) => `${count} MB`,
  scannedPages: (size: string) => `${size} · scanned pages are read with OCR`,
  fromLibrary: 'From your library',
  librarySize: (megabytes: number) => `${megabytes} MB · From your library`,
  uploadingNote:
    'Uploading the video to your library. Long videos can take a while to upload and process.',
  excludeLabel: 'Exclude pages or slides (optional)',
  excludePlaceholder: 'e.g. 1, 3-5',
  excludeHint: 'Excluded pages won’t be included in your microreels.',
  focusLabel: 'Focus (optional)',
  focusPlaceholderVideo: 'e.g. The safety briefing only; new starters.',
  focusPlaceholderDocument:
    'e.g. Sections 2-4 only; new starters; keep the callouts exact.',
  replace: 'Replace',
  remove: 'Remove',
  removeDocument: 'Remove document',
  removeVideo: 'Remove video',
  removeLibraryDocument: 'Remove library document',
  removeLibraryVideo: 'Remove library video',
  previousPage: 'Previous page',
  nextPage: 'Next page',
  pageNumber: (page: number, pages: number) =>
    pages ? `Page ${page} of ${pages}` : `Page ${page}`,
  previewOf: (name: string) => `Preview of ${name}`,

  // Title and course settings
  titleLabel: 'Title',
  titlePlaceholder: 'Title',
  courseCategory: 'Course Category',
  noCategory: 'No category',
  categoriesInWorkspace: 'Categories are available in a workspace.',
  loadingCategories: 'Loading course categories…',
  retryCategories: 'Retry categories',
  noCategories: 'No course categories are available.',
  publicCourse: 'Public Course',
  publicCourseHelp:
    'Available in the public course catalogue after publishing.',

  // How the microreels are made
  howToMake: 'How should we make it?',
  videoModeClipping: 'Use original video',
  videoModeClippingHint: 'Use clips with the original audio and aspect ratio',
  videoModeAnimated: 'Create new content',
  videoModeAnimatedHint:
    'Create new visuals and narration from the video’s content',
  howMany: 'How many microreels should we create?',
  howManyGroup: 'How many microreels',
  reelSingle: 'Create one microreel',
  reelSingleDocumentHint: 'Cover the full document in one microreel.',
  reelSingleVideoHint: 'Cover the full video in one microreel.',
  reelPerTopic: 'Create a microreel for each topic',
  reelPerTopicDocumentHint:
    'Split the document into topics and create a separate microreel for each.',
  reelPerTopicVideoHint:
    'Split the video into topics and create a separate microreel for each.',
  perTopicDocumentNote:
    "We'll create one microreel for each topic we find in your document. Before the storyboard is created you can untick any you don't want, or add your own.",
  perTopicVideoNote:
    "We'll create one microreel for each topic we find in your video. Before the storyboard is created you can untick any you don't want, or add your own.",
  length: 'Length',
  // The length is what planning aims for; a microreel is never stopped for running longer.
  lengthHint:
    'A guide for planning. A microreel can run longer when its narration needs it.',
  backgroundMusic: 'Background music',
  noMusic: 'None',
  musicFromLibrary: 'Choose from library…',
  endingTitle: 'End-of-video activity',
  actionsTitle: 'Actions in the end',

  // Customize: visuals, language and voice
  customize: 'Customize visuals, language and voice',
  customizeLanguage: 'Customize language',
  // Still images keep the generated or source pictures; stock footage plays Storyblocks clips instead.
  videoType: 'Video type',
  videoTypeStill: 'Still images',
  videoTypeStillHint: 'AI-generated images or pictures from your source',
  videoTypeStock: 'Stock footage',
  videoTypeStockHint: 'Licensed Storyblocks video clips chosen for each scene',
  visuals: 'Visuals',
  visualsPartialStock: 'Mix source and stock footage',
  visualsPartialStockHint: 'Your text with stock footage in every scene',
  visualsGeneratedStock: 'Stock footage only',
  visualsGeneratedStockHint: 'Scenes use only stock footage',
  imagesUnavailable:
    'Image generation is unavailable. Scenes will use text and graphic layouts.',
  imageStyle: 'Image style',
  imageStyleHelp: 'Sets the look of generated images.',
  language: 'Language',
  dialect: 'Dialect',
  voice: 'Voice',
  noDefaultVoice: (language: string) =>
    `Emma and James don’t speak ${language}. Choose another voice below.`,
  delivery: 'Delivery',
  naturalDelivery: 'Natural',
  naturalDeliveryHint: 'Follows the script',
  emotiveDeliveryHint: 'Emotive voices follow a delivery most closely.',

  // Motion and sound: the choices themselves are the server's words
  motionAndSound: 'Motion & sound',
  motionLead:
    'How your microreels look and move, and their sound effects. You can try another look once the storyboard is ready.',
  visualDirection: 'Visual direction',
  styleFamily: 'Style',
  anyStyle: 'Let Studio choose',
  anyStyleHint: 'Studio picks a style and keeps it for every microreel.',
  motionAmount: 'Motion amount',
  colors: 'Colors',
  palette: 'Palette',
  soundEffects: 'Sound effects',
  soundEffectsHelp:
    'Short sounds that follow what moves on screen, under the narration. They work with or without background music.',

  // The choices offered when the server's catalog can't be read
  lengthShort: '30-60 sec',
  lengthMedium: '1-2 min',
  lengthLong: '2-5 min',
  endingKnowledgeCheck: 'Knowledge check',
  endingPolicyAttestation: 'Policy attestation',
  endingNone: 'None',
  visualsSource: 'Use source content',
  visualsSourceHint: 'Scenes show only your content',
  visualsPartial: 'Mix source and generated media',
  visualsPartialHint: 'Your text with an AI image in every scene',
  visualsGenerated: 'Generated media only',
  visualsGeneratedHint: 'Scenes use only generated media',

  // What the form asks for before it creates a job
  titleRequired: 'Give the microreel a title.',
  promptRequired: 'Describe what this microreel should cover.',
  sourceRequired: 'Upload a file, or switch to a prompt.',
  chooseLibraryVoice: (language: string) =>
    `Choose a voice. Emma and James don’t speak ${language}.`,
  uploadDocument: 'Upload a PDF, Word (.docx) or PowerPoint (.pptx) file.',
  uploadDocumentOrVideo:
    'Upload a PDF, Word (.docx) or PowerPoint (.pptx) file, or an MP4 video.',
  chooseDocument: 'Choose a PDF, Word (.docx) or PowerPoint (.pptx) document.',
  chooseDocumentOrVideo:
    'Choose a PDF, Word (.docx) or PowerPoint (.pptx) document, or an MP4 video.',
  videoTooLarge: (limitMb: number) =>
    `The video is larger than the ${limitMb} MB limit.`,
  documentTooLarge: (limitMb: number) =>
    `The document is larger than the ${limitMb} MB limit.`,
  pagesFormat: 'Enter page numbers or ranges, such as 1, 3-5.',
  pagesRange: 'Use positive page numbers and valid ranges.',

  // A source video's upload to the library
  uploadFailed: 'We couldn’t upload the video. Please try again.',
  stillProcessing:
    'The video is still processing. Choose it from your library in a few minutes.',
  uploadCancelled: 'The video upload was cancelled.',

  // The voice library
  searchVoices: 'Search voices',
  searchVoicesPlaceholder: 'Search by name, accent or style',
  voiceGender: 'Voice gender',
  allGenders: 'All',
  female: 'Female',
  male: 'Male',
  neutral: 'Neutral',
  emotiveVoices: 'Emotive voices',
  emotive: 'Emotive',
  loadingVoices: 'Loading voices…',
  noLibraryVoices: 'No library voices speak this language yet.',
  noMatchingVoices: 'No voices match your search.',
  voiceCount: (count: number) =>
    count === 1 ? '1 voice' : `${count.toLocaleString()} voices`,
  listenTo: (name: string) => `Listen to ${name}`,
  stopSample: (name: string) => `Stop ${name}'s sample`,
  listen: 'Listen',
  stop: 'Stop',
  showMoreVoices: 'Show more voices',
};

const welsh: typeof english = {
  pageTitle: 'Creu microreel',
  lead: 'Adolygwch a golygwch eich bwrdd stori cyn cynhyrchu eich microreel. Defnyddir credydau AI wrth greu neu ail-greu cynnwys.',
  project: (name: string) => `Prosiect: ${name}`,
  cancel: 'Canslo',
  createStoryboard: 'Creu bwrdd stori',
  findTopics: 'Dod o hyd i bynciau',
  starting: 'Yn dechrau…',
  uploadingVideo: 'Yn llwytho’r fideo i fyny…',

  source: 'Ffynhonnell',
  sourcePrompt: 'Pwnc neu anogwr',
  sourcePromptHint: 'Disgrifiwch bwnc mewn ychydig linellau',
  sourceFile: 'Llwytho ffeil i fyny',
  sourceFileHint: 'PDF, Word neu PowerPoint',
  sourceFileVideoHint: 'PDF, Word, PowerPoint neu fideo MP4',
  hintPrompt: 'Byddwn yn ysgrifennu’r microreel o’ch anogwr.',
  hintClipping: 'Faint o ddata + hyd = nifer y microreels',
  hintVideo:
    'Byddwn yn ysgrifennu’r microreel o’r hyn a ddywedir yn eich fideo.',
  hintDocumentOrVideo:
    'Byddwn yn ysgrifennu’r microreel o’ch dogfen neu’ch fideo.',
  hintDocument:
    'Mae lliwiau a logo eich gweithle (Gosodiadau) ac arddull eich dogfen yn llywio’r brandio.',
  promptLabel: 'Beth ddylai’r microreel hwn ei drafod?',
  promptPlaceholder:
    'e.e. Esboniwch sut i ymateb i larwm tân, gan gynnwys llwybrau gadael, mannau ymgynnull, a phwy i gysylltu â nhw.',
  emptyTitle: 'Oes gennych chi gynnwys i adeiladu arno?',
  dropDocument: 'Gollyngwch ddogfen yma neu borwch',
  dropDocumentOrVideo: 'Gollyngwch ddogfen neu fideo yma, neu borwch',
  uploadFile: 'Llwytho ffeil i fyny',
  formatsDocumentsAndVideos: 'PDF, DOCX, PPTX neu MP4',
  limitsDocumentsAndVideos: (documentMb: number, videoMb: number) =>
    `Dogfennau hyd at ${documentMb} MB · fideos hyd at ${videoMb} MB`,
  formatsDocuments: (documentMb: number) =>
    `PDF, DOCX neu PPTX · hyd at ${documentMb} MB`,
  kilobytes: (count: number) => `${count} KB`,
  megabytes: (count: string) => `${count} MB`,
  scannedPages: (size: string) =>
    `${size} · darllenir tudalennau wedi’u sganio ag OCR`,
  fromLibrary: 'O’ch llyfrgell',
  librarySize: (megabytes: number) => `${megabytes} MB · O’ch llyfrgell`,
  uploadingNote:
    'Yn llwytho’r fideo i fyny i’ch llyfrgell. Gall fideos hir gymryd tipyn o amser i’w llwytho i fyny a’u prosesu.',
  excludeLabel: 'Hepgor tudalennau neu sleidiau (dewisol)',
  excludePlaceholder: 'e.e. 1, 3-5',
  excludeHint: 'Ni fydd y tudalennau a hepgorir yn rhan o’ch microreels.',
  focusLabel: 'Ffocws (dewisol)',
  focusPlaceholderVideo:
    'e.e. Y sesiwn friffio diogelwch yn unig; staff newydd.',
  focusPlaceholderDocument:
    'e.e. Adrannau 2-4 yn unig; staff newydd; cadwch y blychau amlygu air am air.',
  replace: 'Amnewid',
  remove: 'Tynnu',
  removeDocument: 'Tynnu’r ddogfen',
  removeVideo: 'Tynnu’r fideo',
  removeLibraryDocument: 'Tynnu dogfen y llyfrgell',
  removeLibraryVideo: 'Tynnu fideo’r llyfrgell',
  previousPage: 'Tudalen flaenorol',
  nextPage: 'Tudalen nesaf',
  pageNumber: (page: number, pages: number) =>
    pages ? `Tudalen ${page} o ${pages}` : `Tudalen ${page}`,
  previewOf: (name: string) => `Rhagolwg o ${name}`,

  titleLabel: 'Teitl',
  titlePlaceholder: 'Teitl',
  courseCategory: 'Categori’r Cwrs',
  noCategory: 'Dim categori',
  categoriesInWorkspace: 'Mae categorïau ar gael mewn gweithle.',
  loadingCategories: 'Yn llwytho categorïau cyrsiau…',
  retryCategories: 'Rhoi cynnig arall ar y categorïau',
  noCategories: 'Nid oes categorïau cyrsiau ar gael.',
  publicCourse: 'Cwrs Cyhoeddus',
  publicCourseHelp: 'Ar gael yng nghatalog y cyrsiau cyhoeddus ar ôl cyhoeddi.',

  howToMake: 'Sut ddylen ni ei wneud?',
  videoModeClipping: 'Defnyddio’r fideo gwreiddiol',
  videoModeClippingHint:
    'Defnyddio clipiau gyda’r sain a’r gymhareb agwedd wreiddiol',
  videoModeAnimated: 'Creu cynnwys newydd',
  videoModeAnimatedHint: 'Creu delweddau a naratif newydd o gynnwys y fideo',
  howMany: 'Faint o microreels ddylen ni eu creu?',
  howManyGroup: 'Faint o microreels',
  reelSingle: 'Creu un microreel',
  reelSingleDocumentHint: 'Trafod y ddogfen gyfan mewn un microreel.',
  reelSingleVideoHint: 'Trafod y fideo cyfan mewn un microreel.',
  reelPerTopic: 'Creu microreel ar gyfer pob pwnc',
  reelPerTopicDocumentHint:
    'Rhannu’r ddogfen yn bynciau a chreu microreel ar wahân ar gyfer pob un.',
  reelPerTopicVideoHint:
    'Rhannu’r fideo yn bynciau a chreu microreel ar wahân ar gyfer pob un.',
  perTopicDocumentNote:
    'Byddwn yn creu un microreel ar gyfer pob pwnc a welwn yn eich dogfen. Cyn i’r bwrdd stori gael ei greu, gallwch ddad-dicio unrhyw rai nad ydych eu heisiau, neu ychwanegu eich rhai eich hun.',
  perTopicVideoNote:
    'Byddwn yn creu un microreel ar gyfer pob pwnc a welwn yn eich fideo. Cyn i’r bwrdd stori gael ei greu, gallwch ddad-dicio unrhyw rai nad ydych eu heisiau, neu ychwanegu eich rhai eich hun.',
  length: 'Hyd',
  lengthHint:
    'Canllaw ar gyfer cynllunio. Gall microreel fod yn hirach pan fydd ei naratif angen hynny.',
  backgroundMusic: 'Cerddoriaeth gefndir',
  noMusic: 'Dim',
  musicFromLibrary: 'Dewis o’r llyfrgell…',
  endingTitle: 'Gweithgaredd diwedd y fideo',
  actionsTitle: 'Gweithredoedd ar y diwedd',

  customize: 'Addasu’r delweddau, yr iaith a’r llais',
  customizeLanguage: 'Addasu’r iaith',
  videoType: 'Math o fideo',
  videoTypeStill: 'Delweddau llonydd',
  videoTypeStillHint:
    'Delweddau a gynhyrchir gan AI neu luniau o’ch ffynhonnell',
  videoTypeStock: 'Fideo stoc',
  videoTypeStockHint:
    'Clipiau fideo Storyblocks trwyddedig wedi’u dewis ar gyfer pob golygfa',
  visuals: 'Delweddau',
  visualsPartialStock: 'Cymysgu’r ffynhonnell a fideo stoc',
  visualsPartialStockHint: 'Eich testun gyda fideo stoc ym mhob golygfa',
  visualsGeneratedStock: 'Fideo stoc yn unig',
  visualsGeneratedStockHint: 'Mae’r golygfeydd yn defnyddio fideo stoc yn unig',
  imagesUnavailable:
    'Nid yw cynhyrchu delweddau ar gael. Bydd golygfeydd yn defnyddio cynlluniau testun a graffeg.',
  imageStyle: 'Arddull delwedd',
  imageStyleHelp: 'Yn gosod golwg y delweddau a gynhyrchir.',
  language: 'Iaith',
  dialect: 'Tafodiaith',
  voice: 'Llais',
  noDefaultVoice: (language: string) =>
    `Nid yw Emma a James yn siarad ${language}. Dewiswch lais arall isod.`,
  delivery: 'Traddodi',
  naturalDelivery: 'Naturiol',
  naturalDeliveryHint: 'Yn dilyn y sgript',
  emotiveDeliveryHint: 'Lleisiau emosiynol sy’n dilyn y traddodi agosaf.',

  motionAndSound: 'Symudiad a sain',
  motionLead:
    'Sut mae eich microreels yn edrych ac yn symud, a’u heffeithiau sain. Gallwch roi cynnig ar olwg arall pan fydd y bwrdd stori’n barod.',
  visualDirection: 'Cyfeiriad gweledol',
  styleFamily: 'Arddull',
  anyStyle: 'Gadael i Studio ddewis',
  anyStyleHint:
    'Mae Studio yn dewis arddull ac yn ei chadw ar gyfer pob microreel.',
  motionAmount: 'Faint o symud',
  colors: 'Lliwiau',
  palette: 'Palet',
  soundEffects: 'Effeithiau sain',
  soundEffectsHelp:
    'Synau byr sy’n dilyn yr hyn sy’n symud ar y sgrin, o dan y naratif. Maen nhw’n gweithio gyda cherddoriaeth gefndir neu hebddi.',

  lengthShort: '30-60 eiliad',
  lengthMedium: '1-2 munud',
  lengthLong: '2-5 munud',
  endingKnowledgeCheck: 'Gwiriad gwybodaeth',
  endingPolicyAttestation: 'Cadarnhau polisi',
  endingNone: 'Dim',
  visualsSource: 'Defnyddio cynnwys y ffynhonnell',
  visualsSourceHint: 'Mae’r golygfeydd yn dangos eich cynnwys chi yn unig',
  visualsPartial: 'Cymysgu’r ffynhonnell a chyfryngau a gynhyrchir',
  visualsPartialHint: 'Eich testun gyda delwedd AI ym mhob golygfa',
  visualsGenerated: 'Cyfryngau a gynhyrchir yn unig',
  visualsGeneratedHint:
    'Mae’r golygfeydd yn defnyddio cyfryngau a gynhyrchir yn unig',

  titleRequired: 'Rhowch deitl i’r microreel.',
  promptRequired: 'Disgrifiwch beth ddylai’r microreel hwn ei drafod.',
  sourceRequired: 'Llwythwch ffeil i fyny, neu newidiwch i anogwr.',
  chooseLibraryVoice: (language: string) =>
    `Dewiswch lais. Nid yw Emma a James yn siarad ${language}.`,
  uploadDocument:
    'Llwythwch ffeil PDF, Word (.docx) neu PowerPoint (.pptx) i fyny.',
  uploadDocumentOrVideo:
    'Llwythwch ffeil PDF, Word (.docx) neu PowerPoint (.pptx), neu fideo MP4, i fyny.',
  chooseDocument: 'Dewiswch ddogfen PDF, Word (.docx) neu PowerPoint (.pptx).',
  chooseDocumentOrVideo:
    'Dewiswch ddogfen PDF, Word (.docx) neu PowerPoint (.pptx), neu fideo MP4.',
  videoTooLarge: (limitMb: number) =>
    `Mae’r fideo yn fwy na’r terfyn o ${limitMb} MB.`,
  documentTooLarge: (limitMb: number) =>
    `Mae’r ddogfen yn fwy na’r terfyn o ${limitMb} MB.`,
  pagesFormat: 'Rhowch rifau tudalennau neu ystodau, fel 1, 3-5.',
  pagesRange: 'Defnyddiwch rifau tudalennau positif ac ystodau dilys.',

  uploadFailed:
    'Nid oedd modd i ni lwytho’r fideo i fyny. Rhowch gynnig arall arni.',
  stillProcessing:
    'Mae’r fideo yn dal i gael ei brosesu. Dewiswch ef o’ch llyfrgell ymhen ychydig funudau.',
  uploadCancelled: 'Cafodd llwytho’r fideo i fyny ei ganslo.',

  searchVoices: 'Chwilio lleisiau',
  searchVoicesPlaceholder: 'Chwilio yn ôl enw, acen neu arddull',
  voiceGender: 'Rhywedd y llais',
  allGenders: 'Pob un',
  female: 'Benyw',
  male: 'Gwryw',
  neutral: 'Niwtral',
  emotiveVoices: 'Lleisiau emosiynol',
  emotive: 'Emosiynol',
  loadingVoices: 'Yn llwytho lleisiau…',
  noLibraryVoices: 'Nid oes lleisiau llyfrgell yn siarad yr iaith hon eto.',
  noMatchingVoices: 'Nid oes lleisiau’n cyfateb i’ch chwiliad.',
  // Welsh counts take the singular noun.
  voiceCount: (count: number) => `${count.toLocaleString()} llais`,
  listenTo: (name: string) => `Gwrando ar ${name}`,
  stopSample: (name: string) => `Atal sampl ${name}`,
  listen: 'Gwrando',
  stop: 'Atal',
  showMoreVoices: 'Dangos rhagor o leisiau',
};

export const createText: typeof english = getLocalizationBasedOnLang(
  english,
  welsh,
);
